// NobleProgressRing - Circular progress indicator
// Use cases: Torque gauges, completion %, health scores, KPIs
// Features: Animated, color-coded thresholds, customizable size

import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Typography } from 'antd';
import { NobleColors, NobleTypography } from '../../../theme/noble-tokens';

const { Text } = Typography;

export interface NobleProgressRingProps {
  value: number; // 0-100
  max?: number; // Maximum value (default 100)
  size?: 'small' | 'medium' | 'large' | number; // px
  thickness?: number; // Ring thickness in px
  color?: string; // Custom color (overrides thresholds)
  label?: React.ReactNode;
  subLabel?: React.ReactNode;
  showValue?: boolean;
  showPercentage?: boolean;
  thresholds?: {
    low: number; // 0-low: green
    medium: number; // low-medium: yellow
    high: number; // medium-high: red
  };
  colors?: {
    low: string;
    medium: string;
    high: string;
  };
  animated?: boolean;
  clockwise?: boolean;
  startAngle?: number; // degrees (-90 = top)
  className?: string;
}

// Size presets
const SIZE_PRESETS = {
  small: 120,
  medium: 200,
  large: 280,
};

// Default thresholds
const DEFAULT_THRESHOLDS = {
  low: 60,
  medium: 85,
  high: 100,
};

// Default colors
const DEFAULT_COLORS = {
  low: NobleColors.semantic.operationalGreen,
  medium: NobleColors.semantic.warningAmber,
  high: NobleColors.semantic.criticalRed,
};

// Styled Components
const RingContainer = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SVGRing = styled.svg`
  transform: rotate(-90deg); // Start from top
  overflow: visible;
`;

const CenterContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
`;

const ValueText = styled(Text)<{ $size: number }>`
  &&& {
    font-size: ${({ $size }) => {
      if ($size <= 120) return '24px';
      if ($size <= 200) return '32px';
      return '48px';
    }};
    font-weight: 700;
    font-family: ${NobleTypography.fontFamily.mono};
    line-height: 1;
    color: ${NobleColors.accent.steelGrayDark};
  }
`;

const LabelText = styled(Text)<{ $size: number }>`
  &&& {
    font-size: ${({ $size }) => {
      if ($size <= 120) return '10px';
      if ($size <= 200) return '12px';
      return '14px';
    }};
    font-weight: 600;
    color: ${NobleColors.accent.steelGray};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 4px;
  }
`;

const SubLabelText = styled(Text)<{ $size: number }>`
  &&& {
    font-size: ${({ $size }) => {
      if ($size <= 120) return '9px';
      if ($size <= 200) return '10px';
      return '12px';
    }};
    color: ${NobleColors.accent.steelGray};
    margin-top: 2px;
  }
`;

// Helper function to calculate circle properties
const getCircleProps = (size: number, thickness: number) => {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  return { radius, circumference, center };
};

// Helper function to get color based on value and thresholds
const getColorForValue = (
  value: number,
  thresholds: { low: number; medium: number; high: number },
  colors: { low: string; medium: string; high: string }
): string => {
  if (value <= thresholds.low) return colors.low;
  if (value <= thresholds.medium) return colors.medium;
  return colors.high;
};

// Main Component
export const NobleProgressRing: React.FC<NobleProgressRingProps> = ({
  value,
  max = 100,
  size = 'medium',
  thickness = 12,
  color,
  label,
  subLabel,
  showValue = true,
  showPercentage = false,
  thresholds = DEFAULT_THRESHOLDS,
  colors = DEFAULT_COLORS,
  animated = true,
  clockwise = true,
  startAngle = -90,
  className,
}) => {
  const [animatedValue, setAnimatedValue] = useState(animated ? 0 : value);

  // Animate value change
  useEffect(() => {
    if (!animated) {
      setAnimatedValue(value);
      return;
    }

    const duration = 1000; // ms
    const steps = 60;
    const increment = (value - animatedValue) / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setAnimatedValue(value);
        clearInterval(interval);
      } else {
        setAnimatedValue((prev) => prev + increment);
      }
    }, duration / steps);

    return () => clearInterval(interval);
  }, [value, animated]);

  // Calculate size
  const sizeInPx = typeof size === 'number' ? size : SIZE_PRESETS[size];

  // Calculate circle properties
  const { radius, circumference, center } = getCircleProps(sizeInPx, thickness);

  // Calculate progress
  const percentage = Math.min((animatedValue / max) * 100, 100);
  const progressOffset = circumference - (percentage / 100) * circumference;

  // Determine color
  const ringColor = color || getColorForValue(percentage, thresholds, colors);

  // Format display value
  const displayValue = showPercentage
    ? `${Math.round(percentage)}%`
    : Math.round(animatedValue);

  return (
    <RingContainer $size={sizeInPx} className={className}>
      <SVGRing width={sizeInPx} height={sizeInPx} viewBox={`0 0 ${sizeInPx} ${sizeInPx}`}>
        {/* Background ring */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={NobleColors.background.level2Light}
          strokeWidth={thickness}
          opacity={0.3}
        />

        {/* Progress ring */}
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={thickness}
          strokeDasharray={circumference}
          strokeDashoffset={progressOffset}
          strokeLinecap="round"
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: progressOffset }}
          transition={{ duration: animated ? 1 : 0, ease: 'easeOut' }}
          style={{
            transformOrigin: 'center',
            transform: clockwise ? 'none' : 'scaleX(-1)',
          }}
        />
      </SVGRing>

      {/* Center content */}
      <CenterContent>
        {showValue && (
          <ValueText $size={sizeInPx}>
            {displayValue}
          </ValueText>
        )}
        {label && (
          <LabelText $size={sizeInPx}>
            {label}
          </LabelText>
        )}
        {subLabel && (
          <SubLabelText $size={sizeInPx}>
            {subLabel}
          </SubLabelText>
        )}
      </CenterContent>
    </RingContainer>
  );
};

// Pre-configured variants

interface TorqueGaugeProps extends Omit<NobleProgressRingProps, 'label' | 'thresholds' | 'colors'> {
  maxTorque?: number;
}

export const TorqueGauge: React.FC<TorqueGaugeProps> = ({ maxTorque = 100, ...props }) => (
  <NobleProgressRing
    {...props}
    max={maxTorque}
    label="Torque"
    subLabel={`${maxTorque} Nm`}
    thresholds={{ low: 60, medium: 85, high: 100 }}
    colors={{
      low: NobleColors.semantic.operationalGreen,
      medium: NobleColors.semantic.warningAmber,
      high: NobleColors.semantic.criticalRed,
    }}
  />
);

interface HealthScoreProps extends Omit<NobleProgressRingProps, 'label' | 'thresholds' | 'colors'> {}

export const HealthScore: React.FC<HealthScoreProps> = (props) => (
  <NobleProgressRing
    {...props}
    label="Health Score"
    showPercentage
    thresholds={{ low: 70, medium: 90, high: 100 }}
    colors={{
      low: NobleColors.semantic.criticalRed,
      medium: NobleColors.semantic.warningAmber,
      high: NobleColors.semantic.operationalGreen,
    }}
  />
);

interface CompletionRingProps extends Omit<NobleProgressRingProps, 'label' | 'color'> {}

export const CompletionRing: React.FC<CompletionRingProps> = (props) => (
  <NobleProgressRing
    {...props}
    label="Complete"
    showPercentage
    color={NobleColors.primary.commandBlue}
  />
);

interface CapacityRingProps extends Omit<NobleProgressRingProps, 'label' | 'thresholds' | 'colors'> {}

export const CapacityRing: React.FC<CapacityRingProps> = (props) => (
  <NobleProgressRing
    {...props}
    label="Capacity"
    showPercentage
    thresholds={{ low: 70, medium: 90, high: 100 }}
    colors={{
      low: NobleColors.semantic.operationalGreen,
      medium: NobleColors.semantic.warningAmber,
      high: NobleColors.semantic.criticalRed,
    }}
  />
);

